import { Injectable, Output, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of, BehaviorSubject, ReplaySubject } from 'rxjs';

import { map, catchError, share } from 'rxjs/operators';
import { SharedService } from './sharedService';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Role } from '../entities/role';
import { User } from '../entities/user';
import { UserService } from './user.service';
import { apiHost } from '../util/app-config';
import { environment } from 'src/environments/environment';


/** Server's answer to "which identity providers does this deployment offer". */
export interface AuthProviders {
  sso: boolean;
  local: boolean;
  allowedMfaMethods: string[];
  passwordMinLength: number;
}

/**
 * Result of the password step. Note there is no token here on purpose: the server
 * returns a short-lived challenge, and only the second-factor call returns a
 * session. `stage` says which of the two follow-ups is required.
 */
export interface LocalLoginResult {
  statusCode: number;
  /**
   * 'complete' means the password was enough: the device was remembered from
   * an earlier sign-in that DID present a second factor, so this response
   * carries a token and no challenge. Every other stage carries a challenge
   * and no token -- a password alone can never produce a session otherwise.
   */
  stage: 'mfa-verify' | 'mfa-enrolment' | 'complete';
  challenge: string;
  /** Present only on stage 'complete'. */
  token?: string;
  usrToken?: string;
  mfaMethod?: 'totp' | 'email';
  recoveryAvailable?: boolean;
  /** Whether the deployment permits an emailed code as a fallback factor. */
  emailFallbackAvailable?: boolean;
  /**
   * Days a remembered device may skip the second factor, or 0/absent for never.
   * Absent is what an older fusion-server sends, and must read as off.
   */
  trustedDeviceDays?: number;
  allowedMfaMethods?: string[];
}

@Injectable()
export class AuthService {
  private username$ = new BehaviorSubject<string>(undefined);
  private userRole$ = new BehaviorSubject<string>(undefined);

  private authChangedSource = new ReplaySubject<boolean>(1);
  authChanged = this.authChangedSource.asObservable();
  accessToken: string
  constructor(private http: HttpClient, private sharedService: SharedService, private userService:UserService, public decoder: JwtHelperService) {  this.accessToken = sharedService.getAccessToken() }
 
  /**
   * Which identity providers this deployment offers.
   *
   * Unauthenticated, and safe to call before anything else -- it is how one build
   * serves both an SSO-only and a local-enabled environment instead of the choice
   * being compiled into environment.ts.
   */
  providers$(): Observable<AuthProviders> {
    return this.http.get<AuthProviders>(this.sharedService.apiUrlLogin('web/auth/providers'));
  }

  /**
   * Step one of local sign-in: prove the password.
   *
   * Deliberately does NOT store a session. The server answers with a challenge
   * token whose audience the API guard rejects, so a correct password alone is not
   * a session -- `verifyMfa` or `verifyRecovery` completes the login. The previous
   * body of this method stored `result.token` straight into localStorage against
   * an endpoint that no longer exists (`auth/login` was unmounted server-side, and
   * the tokens it minted carried no audience/issuer so the API rejected them).
   */
  localLogin(username: string, password: string): Observable<LocalLoginResult> {
    return this.http.post<LocalLoginResult>(
      this.sharedService.apiUrlLogin('web/auth/local/login'),
      { username: username, password: password });
  }

  /** Step two: the second factor. This is what returns a usable session. */
  /**
   * Asks for a sign-in code by email from the code screen.
   *
   * Available to any enrolled account, not only those that chose email -- see
   * the note on `role:'local-auth', cmd:'mfa-email-code'` for the trade-off that
   * buys. Calling it again resends and invalidates the previous code, so this is
   * also the resend path.
   */
  requestEmailCode(challenge: string): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/mfa/email-code'),
      { challenge: challenge });
  }

  verifyMfa(challenge: string, code: string, rememberDevice = false): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/mfa'),
      { challenge: challenge, code: code, rememberDevice: rememberDevice });
  }

  /** Lost-device path. Spends one recovery code and returns a session. */
  verifyRecovery(challenge: string, recoveryCode: string, rememberDevice = false): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/mfa/recovery'),
      { challenge: challenge, recoveryCode: recoveryCode, rememberDevice: rememberDevice });
  }

  /** Begins enrolment. For 'totp' returns a QR data URL and the secret; for 'email' sends a code. */
  enrolBegin(challenge: string, method: 'totp' | 'email'): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/mfa/enrol/begin'),
      { challenge: challenge, method: method });
  }

  /** Confirms enrolment. Returns the one-time recovery codes and a session. */
  enrolConfirm(challenge: string, code: string): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/mfa/enrol/confirm'),
      { challenge: challenge, code: code });
  }

  /**
   * Reads an invitation so the page can prefill.
   *
   * POST rather than GET /:token because the token is a credential: the server's
   * request logger redacts `?token=` but not path segments, so a path-borne token
   * would be written to the access log.
   */
  invitationLookup(token: string): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/invitation/lookup'), { token: token });
  }

  /** Sets the password from an invitation. Returns an enrolment challenge, not a session. */
  invitationAccept(token: string, password: string): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/invitation/accept'),
      { token: token, password: password });
  }

  /** Always succeeds, whether or not the address is known. Never branch the UI on the result. */
  forgotPassword(username: string): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/password/forgot'), { username: username });
  }

  resetPassword(token: string, password: string): Observable<any> {
    return this.http.post<any>(
      this.sharedService.apiUrlLogin('web/auth/local/password/reset'),
      { token: token, password: password });
  }

  /**
   * Stores a completed session.
   *
   * Uses the same key and the same updateAccessToken() notification as the SSO
   * path in login.component.ts, so everything downstream -- the interceptor, the
   * route guard, the header -- cannot tell the two providers apart. That is the
   * whole point: the token is the same shape either way.
   */
  storeSession(token: string) {
    localStorage.setItem('access_token', token);
    this.sharedService.updateAccessToken();
  }

  async requestToken(token) {
    let login$ = this.http.get<any>(apiHost()+'/web/sso_auth/renewToken/' + token)
    const res = await login$.toPromise().then(res => res);
    return res.usrToken;
  }
  // logout() {
  // }

  logout() {
    let accessToken = localStorage.getItem('access_token');
    localStorage.removeItem('access_token');
    return this.http.get<any>(apiHost()+'/web/logout'+ (accessToken ? "/?localToken=" + accessToken : ""))
  }

  public get isloggedIn(): boolean {
    return (localStorage.getItem('access_token') !== null);
  }

  @Output() loggedIn: EventEmitter<boolean> = new EventEmitter();
  setLoginStatus(obj) {
    this.loggedIn.emit(obj)
    if (!obj){
      this.clearLocalStorage();
    }
  }

  @Output() isUserActive: EventEmitter<boolean> = new EventEmitter();
  setUserEnabled(obj) {
    this.isUserActive.emit(obj)
  }

  clearLocalStorage(){
    localStorage.removeItem(this.accessToken)
    localStorage.removeItem('username')
    localStorage.removeItem('userrole')
  }

  setUsername(obj) {
    this.username$.next(obj);
  }

  setUserRole$(obj) {
    this.userRole$.next(obj);
  }

  getUsername$(): Observable<any> {
    return this.username$.asObservable();
  }

  getUserRole$(): Observable<any> {
    return this.userRole$.asObservable();
  }

  getDecodedToken(){
    return this.decoder.decodeToken(this.decoder.tokenGetter() as string)
  }

  getCurrentUser(): Observable<User>{
      return this.userService.getCurrentUser()
  }

 
  getTokenFromNonce(nonce): Observable<boolean> {
    let token$ = this.http.get<any>(this.sharedService.apiUrlLogin('web/sso_auth') + "/?nonce=" + nonce)
    return token$;
  }

  getRedirectLink(): Observable<boolean> {
    return this.http.get<any>(this.sharedService.apiUrlLogin('web/sso_auth')).pipe(catchError(this.handleError<any>('getRedirectLink')))
  }

  handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);
      return throwError(error.error || 'Server error');
    };
  }

  log(message: string) {
    //this.messageService.add('HeroService: ' + message);
  }

}
